/**
 * Standard-Layout für Protokoll-PDFs.
 *
 * Bewusst als Handlebars-Vorlage mit den Helpern aus `handlebars-helpers.ts` —
 * Browser-Vorschau und Lambda erzeugen daraus byte-identisches HTML
 * (CLAUDE.md §3.5). Die Vorlage kommt ohne Schleifenlogik über das
 * `formSchema` aus: Sie rendert die aufbereitete `abschnitte`-Liste, die der
 * Aufrufer bereitstellt.
 */

export const STANDARD_LAYOUT_HTML = `<div class="uep-seite">

  <header class="kopf">
    {{#if branding.logoDataUri}}
      <img class="logo" src="{{branding.logoDataUri}}" alt="" />
    {{/if}}
    <div class="kopf-text">
      <h1>Übergabeprotokoll — {{meta.art}}</h1>
      <p class="untertitel">
        {{#wenn meta.uebergabeDatum "gesetzt"}}Übergabe am {{datum meta.uebergabeDatum}}{{/wenn}}
      </p>
    </div>
    <div class="kopf-nummer">
      <span class="nummer">{{meta.protokollNr}}</span>
      {{#wenn branding.firmenname "gesetzt"}}<span class="firma">{{branding.firmenname}}</span>{{/wenn}}
    </div>
  </header>

  <section class="objektblock">
    <h2>Objekt</h2>
    <table class="paare">
      <tr><th>Objektnummer</th><td>{{oder objekt.objektNr "—"}}</td></tr>
      <tr><th>Bezeichnung</th><td>{{oder objekt.bezeichnung "—"}}</td></tr>
      <tr><th>Anschrift</th><td>{{oder objekt.strasse "—"}} · {{objekt.plz}} {{objekt.ort}}</td></tr>
      {{#wenn einheit "gesetzt"}}<tr><th>Einheit</th><td>{{#wenn einheit.einheitNr "gesetzt"}}{{einheit.einheitNr}} · {{/wenn}}{{oder einheit.bezeichnung "—"}}{{#wenn einheit.etage "gesetzt"}} · {{einheit.etage}}{{/wenn}}</td></tr>{{/wenn}}
      <tr><th>Lage</th><td>{{oder einheit.lage objekt.lage "—"}}</td></tr>
      <tr><th>Wohnfläche</th><td>{{#wenn einheit.wohnflaeche "gesetzt"}}{{flaeche einheit.wohnflaeche}}{{else}}{{#wenn objekt.wohnflaeche "gesetzt"}}{{flaeche objekt.wohnflaeche}}{{else}}—{{/wenn}}{{/wenn}}</td></tr>
    </table>
  </section>

  {{#wenn meta.beteiligte "gesetzt"}}
  <section class="beteiligte">
    <h2>Beteiligte</h2>
    <table class="liste">
      <thead><tr><th>Rolle</th><th>Name</th><th>Firma</th></tr></thead>
      <tbody>
        {{#jede meta.beteiligte}}
        <tr><td>{{rolle}}</td><td>{{name}}</td><td>{{oder firma "—"}}</td></tr>
        {{/jede}}
      </tbody>
    </table>
  </section>
  {{/wenn}}

  {{#jede abschnitte}}
  <section class="abschnitt">
    <h2>{{titel}}</h2>

    {{#wenn typ "gleich" "standard"}}
      <table class="paare">
        {{#jede eintraege}}
        <tr><th>{{label}}</th><td>{{{anzeige}}}</td></tr>
        {{/jede}}
      </table>
    {{/wenn}}

    {{#wenn typ "gleich" "wiederholbar"}}
      {{#jede zeilen}}
      <div class="uep-block zeile">
        <h3>{{titel}}</h3>
        <table class="paare">
          {{#jede eintraege}}
          <tr><th>{{label}}</th><td>{{{anzeige}}}</td></tr>
          {{/jede}}
        </table>
      </div>
      {{/jede}}
    {{/wenn}}

    {{#wenn typ "gleich" "tabelle"}}
      <table class="liste">
        <thead><tr>{{#jede spalten}}<th>{{this}}</th>{{/jede}}</tr></thead>
        <tbody>
          {{#jede zeilen}}
          <tr>{{#jede zellen}}<td>{{{this}}}</td>{{/jede}}</tr>
          {{/jede}}
        </tbody>
      </table>
    {{/wenn}}
  </section>
  {{/jede}}

  {{#wenn meta.maengel "gesetzt"}}
  <section class="abschnitt">
    <h2>Mängelübersicht</h2>
    <table class="liste">
      <thead><tr><th>Nr.</th><th>Ort</th><th>Beschreibung</th><th>Kategorie</th><th>Schwere</th><th>Fotos</th></tr></thead>
      <tbody>
        {{#jede meta.maengel}}
        <tr class="uep-mangel">
          <td>{{@nummer}}</td>
          <td>{{oder ort "—"}}</td>
          <td>{{zeilen beschreibung}}</td>
          <td>{{oder kategorie "—"}}</td>
          <td>{{oder schweregrad "—"}}</td>
          <td>{{oder fotoNummern "—"}}</td>
        </tr>
        {{/jede}}
      </tbody>
    </table>
  </section>
  {{/wenn}}

  <section class="unterschriften uep-block">
    <h2>Unterschriften</h2>
    {{#wenn meta.signaturen "gesetzt"}}
    <p class="rechtshinweis">
      Die Unterzeichnenden bestätigen mit ihrer Unterschrift den oben festgehaltenen Zustand.
      Es handelt sich um eine einfache elektronische Signatur im Sinne des § 127 BGB.
      Die Unterschrift bezieht sich auf die Prüfsumme {{meta.hashPraefix}}.
    </p>
    <div class="signaturgitter">
      {{#jede meta.signaturen}}
      <div class="signatur uep-block">
        {{signatur this breite=220}}
        <hr />
        <p class="name">{{name}}</p>
        <p class="rolle">{{rolle}}</p>
        <p class="zeit">{{zeitstempel signiertAm}}</p>
      </div>
      {{/jede}}
    </div>
    {{else}}
    <p class="rechtshinweis">
      Zu diesem Protokoll liegen keine Unterschriften vor. Es hält den erfassten Zustand fest,
      ist aber keine beidseitig bestätigte Erklärung.
      Der Inhalt ist über die Prüfsumme {{meta.hashPraefix}} gebunden.
    </p>
    {{/wenn}}
  </section>

  {{#wenn meta.fotoAnhang "gesetzt"}}
  {{seitenumbruch}}
  <section class="anhang">
    <h2>Fotoanhang</h2>
    <div class="fotogitter">
      {{#jede meta.fotoAnhang}}
      <figure class="uep-block">
        {{bild this breite=250}}
        <figcaption>Foto {{nummer}}{{#wenn beschriftung "gesetzt"}} — {{beschriftung}}{{/wenn}}</figcaption>
      </figure>
      {{/jede}}
    </div>
  </section>
  {{/wenn}}

</div>`;

export const STANDARD_LAYOUT_CSS = `
h1 { font-size: 16pt; margin: 0; color: var(--uep-primaerfarbe); }
h2 { font-size: 12pt; margin: 14pt 0 5pt; padding-bottom: 2pt;
     border-bottom: 1pt solid var(--uep-akzentfarbe); color: var(--uep-primaerfarbe); }
h3 { font-size: 10.5pt; margin: 6pt 0 3pt; }

.kopf { display: flex; align-items: flex-start; gap: 10mm;
        border-bottom: 2pt solid var(--uep-primaerfarbe); padding-bottom: 6pt; }
.kopf .logo { max-height: 18mm; max-width: 45mm; object-fit: contain; }
.kopf-text { flex: 1; }
.untertitel { margin: 2pt 0 0; color: #4b5563; font-size: 9.5pt; }
.kopf-nummer { text-align: right; }
.kopf-nummer .nummer { display: block; font-size: 12pt; font-weight: 700; }
.kopf-nummer .firma { display: block; font-size: 8.5pt; color: #6b7280; }

table.paare { margin: 3pt 0; }
table.paare th { width: 38mm; font-weight: 600; color: #4b5563; vertical-align: top; }
table.paare td { border-bottom: 0.5pt solid #f1f5f9; }

table.liste { margin: 4pt 0; font-size: 9pt; }
table.liste th { background: var(--uep-akzentfarbe); font-weight: 600; }
table.liste td, table.liste th { border: 0.5pt solid #d1d5db; }

.zeile { margin: 5pt 0; padding: 4pt 6pt; background: #fafafa; border-left: 2pt solid var(--uep-akzentfarbe); }

.unterschriften { margin-top: 14pt; }
.rechtshinweis { font-size: 8pt; color: #4b5563; margin: 0 0 8pt; }
.signaturgitter { display: flex; flex-wrap: wrap; gap: 10mm; }
.signatur { width: 70mm; }
.signatur hr { border: none; border-top: 0.75pt solid #111827; margin: 2pt 0 3pt; }
.signatur .name { margin: 0; font-weight: 600; font-size: 9.5pt; }
.signatur .rolle, .signatur .zeit { margin: 0; font-size: 8pt; color: #6b7280; }

.fotogitter { display: flex; flex-wrap: wrap; gap: 6mm; }
.fotogitter figure { margin: 0; width: 66mm; }
.fotogitter figcaption { font-size: 8pt; color: #4b5563; margin-top: 2pt; }
.uep-foto { border: 0.5pt solid #d1d5db; border-radius: 2pt; }
`;
